import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { useTheme } from '../hooks/useTheme.ts';
import { getDescriptionOptions } from './config/proppant.ts';
import { PETRIX_CHANNEL_LABELS, PETRIX_CHANNEL_OPTIONS } from './config/channels.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChemChannel {
  value: string;
  label: string;
  chem_name?: string;
  metric?: string;
}

interface TriggerRow {
  id: string;
    provider: 'petrix' | 'seismos' | 'momentum' | 'scoreboard' | '';
  channel: string;
  condition: '>' | '>=' | '<' | '<=' | '==' | '!=' | '';
  value: string;
  combine: 'none' | 'and' | 'or';
}

interface ActionRow {
  id: string;
  dest_channel: string;
  direction: 'increase' | 'decrease' | '';
  mode: 'by' | 'to' | '';
  unit: 'pct' | 'ppa' | 'gpt' | 'gpm' | 'bpm' | '';
  dest_value: string;
  // Event action fields — null for channel actions
  event_id: number | null;
  params: Record<string, string>;
  // Actions only support 'none' | 'and' — "or" makes no semantic sense for actions
  combine: 'none' | 'and';
  // Carry the change forward to later steps instead of reverting when the
  // current step ends. Only meaningful for slurry rate and chemical ratios.
  cascade: boolean;
}

interface RuleExecution {
  id: number;
  rule_id: number;
  fired_at_ms: number;
  channel: string;
  direction: string;
  mode: string;
  unit: string;
  dest_value: number;
  from_value: number | null;
  to_value: number;
  mqtt_success: number;
  mqtt_error: string | null;
}

interface Rule {
  id: string;
  db_id?: number;
  name: string;
  is_active: boolean;
  collapsed: boolean;
  fire_once_per_stage: boolean;
  rate_pressure_prediction_enabled: boolean;
  fire_until_acknowledgement: boolean;
  accept_on_expire_time: number | null;
  cooldown_seconds: number;
  scope_type: 'pad' | 'wells' | 'stages';
  scope_well_ids: number[];
  scope_stage_ids: number[];
  created_by_user_id?: string;
  triggers: TriggerRow[];
  actions: ActionRow[];
}

interface RulesManagementModalProps {
  show: boolean;
  onHide: () => void;
  padId: number | null;
  chemChannels?: ChemChannel[];
  scoreboardChannels?: { value: string; label: string }[];
  wells?: { id: number; well_name: string }[];
  stages?: { id: number; well_id: number; stage_number: number }[];
  userRole?: string;
}

const SCOPE_TYPE_OPTIONS = [
  { value: 'pad',    label: 'Entire Pad' },
  { value: 'wells',  label: 'Wells'      },
  { value: 'stages', label: 'Stages'     },
];

// ─── Petrix Action Registry ───────────────────────────────────────────────────
// Every param-driven action lives here — add new ones here only, no other code
// changes needed.
//
//   event_id: number  → params ARE the wire payload. Published to the events
//                       topic; Petrix reads them and executes.
//   event_id: null    → params are build inputs. Nothing is published; the
//                       worker consumes them to build a schedule edit, and
//                       Petrix accepts/rejects the resulting rec like any other.

interface EventParamDef {
  key: string;
  label: string;
  unit: string;
  default: string;
  min?: number;
  max?: number;
  step?: number;
  type?: 'number' | 'text' | 'select';   // defaults to number
  options?: string[];                    // 'select' only
}

interface PetrixActionDef {
  event_id: number | null;
  channel: string;   // value used in dest_channel / CHANNEL_LABELS
  label: string;
  params: EventParamDef[];
}

const PETRIX_ACTIONS: PetrixActionDef[] = [
  {
    event_id: 1,
    channel:  'rate_drop',
    label:    'Rate Drop',
    params: [
      { key: 'rateDrop', label: 'Rate Drop', unit: 'bbl', default: '3',  min: 1, max: 10, step: 1 },
      { key: 'dropTime', label: 'Drop Time', unit: 'sec', default: '20', min: 5, max: 90, step: 1 },
    ],
  },
  {
    event_id: null,
    channel:  'diverter_drop',
    label:    'Diverter Drop',
    params: [
      { key: 'clean', label: 'Step Clean', unit: 'bbl', default: '20', min: 1, max: 2000, step: 0.1 },
    ],
  },
  {
    event_id: null,
    channel:  'sand_slug_drop',
    label:    'Sand Slug Drop',
    params: [
      { key: 'clean',      label: 'Step Clean', unit: 'bbl', default: '20',  min: 1, max: 2000, step: 0.1 },
      { key: 'start_conc', label: 'Start Conc', unit: 'ppa', default: '0.5', min: 0, max: 8,    step: 0.1 },
      { key: 'end_conc',   label: 'End Conc',   unit: 'ppa', default: '0.5', min: 0, max: 8,    step: 0.1 },
      // Two-way with clean — see syncParams. Not read by the worker.
      { key: 'step_prop',  label: 'Step Prop',  unit: 'lbs', default: '420', min: 0, max: 500000, step: 1 },
      { key: 'sand_type',  label: 'Sand Type',  unit: '',    default: '',    type: 'select',
        options: getDescriptionOptions() },
    ],
  },
  // Future: { event_id: 2, channel: 'my_event', label: 'My Event', params: [...] }
];

// Rate is deliberately absent: the inserted step always inherits the rate of
// the step being cut, which is what the Petrix step editor shows in practice.
// Blank sand_type is likewise intentional — the worker inherits it from the
// current (or nearest preceding) proppant step, so a rule authored on one pad
// does not hard-code a mesh the next pad is not running.

const actionDef = (channel: string): PetrixActionDef | undefined =>
  PETRIX_ACTIONS.find(x => x.channel === channel);

// ─── Sand slug: clean bbl ⇄ proppant lbs ──────────────────────────────────────
//   lbs = clean(bbl) x 42 gal/bbl x avg conc(ppa)
// The two are two views of one number, so editing either updates the other and
// changing a concentration re-derives lbs. `clean` stays authoritative: it is
// what the worker puts on the inserted step, and step_prop is never read by it.

const GAL_PER_BBL = 42;

const avgConc = (params: Record<string, string>): number =>
  ((parseFloat(params.start_conc) || 0) + (parseFloat(params.end_conc) || 0)) / 2;

const propFromClean = (params: Record<string, string>): string => {
  const conc  = avgConc(params);
  const clean = parseFloat(params.clean) || 0;
  if (conc <= 0 || clean <= 0) return '';
  return String(Math.round(clean * GAL_PER_BBL * conc));
};

const cleanFromProp = (params: Record<string, string>): string => {
  const conc = avgConc(params);
  const lbs  = parseFloat(params.step_prop) || 0;
  // A zero concentration makes the conversion undefined rather than zero, so
  // leave clean alone and let the existing "step clean > 0" check catch it.
  if (conc <= 0 || lbs <= 0) return '';
  return String(Math.round((lbs / (GAL_PER_BBL * conc)) * 100) / 100);
};

// Single entry point for every param edit. Generic for all actions; only the
// sand slug has coupled fields.
const syncParams = (
  channel: string,
  params: Record<string, string>,
  key: string,
  value: string,
): Record<string, string> => {
  const next = { ...params, [key]: value };
  if (channel !== 'sand_slug_drop') return next;
  if (key === 'step_prop')                                   next.clean     = cleanFromProp(next);
  else if (key === 'clean' || key === 'start_conc' || key === 'end_conc') next.step_prop = propFromClean(next);
  return next;
};

// Rules imported or saved before step_prop existed have no stored value, so
// derive one for display rather than showing an empty box.
const paramDisplayValue = (
  channel: string,
  params: Record<string, string>,
  def: EventParamDef,
): string => {
  const stored = params[def.key];
  if (channel === 'sand_slug_drop' && def.key === 'step_prop' && !stored) {
    return propFromClean(params);
  }
  return stored ?? def.default;
};

// Channels that split the current step and insert a new one.
const DROP_CHANNELS = ['diverter_drop', 'sand_slug_drop'];

// Channels whose change can carry forward to later steps instead of reverting
// when the current step ends. Mirrors _cascades() in poster-worker-db.py.
const isCascadable = (channel: string) =>
  channel === 'slurry_rate' || channel.endsWith('_ratio');
const ruleHasCascade = (actions: ActionRow[]) =>
  actions.some(a => isCascadable(a.dest_channel));

// Upper bound for a plain channel action's value; the value must also be > 0.
// null = no range check for this channel.
const destValueMax = (channel: string): number | null =>
  channel === 'slurry_rate' ? 50
  : channel === 'stage_denso' || channel.endsWith('_ratio') ? 10
  : null;

// ─── Constants ────────────────────────────────────────────────────────────────

const PETRIX_BASE_CHANNELS = PETRIX_CHANNEL_OPTIONS;

const STEP_NAME_OPTIONS = [
  { value: '1',  label: 'Acid' },
  { value: '2',  label: 'Acid Spearhead' },
  { value: '3',  label: 'Breakdown' },
  { value: '4',  label: 'Displacement' },
  { value: '5',  label: 'Diverter' },
  { value: '6',  label: 'FET' },
  { value: '7',  label: 'Flush' },
  { value: '8',  label: 'Load' },
  { value: '9',  label: 'Overflush' },
  { value: '10', label: 'Pad' },
  { value: '11', label: 'Pre-Flush' },
  { value: '12', label: 'Pre-Pad' },
  { value: '13', label: 'Proppant' },
  { value: '14', label: 'Pump-in' },
  { value: '15', label: 'Pumpdown' },
  { value: '16', label: 'Rate Drop' },
  { value: '17', label: 'Rate Drop-Proppant' },
  { value: '18', label: 'Rate Drop-Sand Ramp' },
  { value: '19', label: 'Sand Ramp' },
  { value: '20', label: 'Shutdown' },
  { value: '21', label: 'Spacer' },
  { value: '22', label: 'Sweep' },
];

const SEISMOS_CHANNELS = [
  { value: 'eff',        label: 'Efficiency' },
  { value: 'pipe_frict', label: 'Pipe Friction' },
  { value: 'perf_frict', label: 'Perf Friction' },
  { value: 'ehd',        label: 'EHD' },
  { value: 'fdi_magnitude',                   label: 'FDI Magnitude'           },
  { value: 'max_fdi_slope',                   label: 'Max FDI Slope'           },
  { value: 'vfr',                             label: 'VFR'                     },
  { value: 'estimated_parent_child_distance', label: 'Est. Parent-Child Dist.' },
  { value: 'estimated_fdi_velocity',          label: 'Est. FDI Velocity'       },
];

const MOMENTUM_FDI_CHANNELS = [
  { value: 'fdi_detected',                     label: 'FDI Detected'            },
  { value: 'fdi_magnitude',                   label: 'FDI Magnitude'           },
  { value: 'max_fdi_slope',                   label: 'Max FDI Slope'           },
  { value: 'vfr',                             label: 'VFR'                     },
  { value: 'estimated_parent_child_distance', label: 'Est. Parent-Child Dist.' },
  { value: 'estimated_fdi_velocity',          label: 'Est. FDI Velocity'       },
];

const CONDITIONS = [
  { value: '>',  label: '>' },
  { value: '>=', label: '>=' },
  { value: '<',  label: '<' },
  { value: '<=', label: '<=' },
  { value: '==', label: '=' },
  { value: '!=', label: '≠' },
];
const DIRECTIONS             = [{ value: 'increase', label: 'Increase' }, { value: 'decrease', label: 'Decrease' }];
const MODES                  = [{ value: 'by', label: 'by' }, { value: 'to', label: 'to' }];
const UNITS                  = [
  { value: 'pct', label: '%'   },
  { value: 'ppa', label: 'ppa' },
  { value: 'gpt', label: 'gpt' },
  { value: 'gpm', label: 'gpm' },
  { value: 'bpm', label: 'bpm' },
];
const TRIGGER_COMBINE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'and',  label: 'And'  },
  { value: 'or',   label: 'Or'   },
];
const ACTION_COMBINE_OPTIONS  = [
  { value: 'none', label: 'None' },
  { value: 'and',  label: 'And'  },
];

const COOLDOWN_MIN     = 10;
const COOLDOWN_DEFAULT = 60;

// ─── Auto-accept countdown ────────────────────────────────────────────────────
// State and payload are milliseconds (matching the DB column and the Petrix
// acceptOnExpireTime key); the UI is seconds. NULL or -1 means "off" — the rec
// behaves like every other one and waits for a manual accept/reject.
const AUTO_ACCEPT_MIN_S     = 1;
const AUTO_ACCEPT_MAX_S     = 60;
const AUTO_ACCEPT_DEFAULT_S = 10;
const aoetSecs = (ms: number | null | undefined) =>
  ms != null && ms > 0 ? Math.round(ms / 1000) : null;

// ─── Factory helpers ──────────────────────────────────────────────────────────

const makeTrigger = (): TriggerRow => ({
  id: uuidv4(), provider: '', channel: '', condition: '', value: '', combine: 'none',
});
const makeAction = (): ActionRow => ({
  id: uuidv4(), dest_channel: '', direction: '', mode: '', unit: '', dest_value: '',
  event_id: null, params: {}, combine: 'none', cascade: false,
});
const makeRule = (index: number): Rule => ({
  id: uuidv4(), name: `Rule ${index}`, is_active: true, collapsed: false,
  fire_once_per_stage: false, fire_until_acknowledgement: false,
  rate_pressure_prediction_enabled: false,
  accept_on_expire_time: null,
  cooldown_seconds: COOLDOWN_DEFAULT,
  scope_type: 'pad', scope_well_ids: [], scope_stage_ids: [],
  triggers: [makeTrigger()], actions: [makeAction()],
});

// ─── Helper: build default params for a given channel ────────────────────────
// Keyed by channel, not event_id, so it also covers actions whose event_id is
// null. Returns {} for anything not in the registry, so plain channel actions
// are unaffected.
const defaultParams = (channel: string): Record<string, string> =>
  Object.fromEntries((actionDef(channel)?.params ?? []).map(p => [p.key, p.default]));

// ─── Component ────────────────────────────────────────────────────────────────

const RulesManagementModal: React.FC<RulesManagementModalProps> = ({
  show, onHide, padId, chemChannels = [], scoreboardChannels = [], userRole = 'Viewer', wells = [], stages = [],
}) => {
  const { theme } = useTheme();
  const isDark     = theme === 'dark';
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api';

  const [rules,            setRules]            = useState<Rule[]>([makeRule(1)]);
  const [saving,           setSaving]           = useState(false);
  const [executions,       setExecutions]       = useState<RuleExecution[]>([]);
  const [editingNameId,    setEditingNameId]    = useState<string | null>(null);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showExecDrawer,   setShowExecDrawer]   = useState(false);
  const [execFilterRule,   setExecFilterRule]   = useState<string>('all');
  const [execFilterResult, setExecFilterResult] = useState<string>('all');
  const [execSortDesc,     setExecSortDesc]     = useState(true);
  const [paramErrors,      setParamErrors]      = useState<Record<string, string>>({});
  // Rule id awaiting confirmation before auto-accept is switched on
  const [confirmAutoAcceptId, setConfirmAutoAcceptId] = useState<string | null>(null);
  const importRef        = useRef<HTMLInputElement>(null);
  const savedSnapshotRef = useRef<string>('');

  // ── Dirty-check ───────────────────────────────────────────────────────────
  const rulesSnapshot = (rs: Rule[]) => JSON.stringify(
    rs.map(r => ({
      name: r.name, is_active: r.is_active,
      fire_once_per_stage: r.fire_once_per_stage,
      rate_pressure_prediction_enabled: r.rate_pressure_prediction_enabled,
      fire_until_acknowledgement: r.fire_until_acknowledgement,
      accept_on_expire_time: r.accept_on_expire_time,
      cooldown_seconds: r.cooldown_seconds,
      scope_type: r.scope_type,
      scope_well_ids: [...r.scope_well_ids].sort((a, b) => a - b),
      scope_stage_ids: [...r.scope_stage_ids].sort((a, b) => a - b),
      triggers: r.triggers.map(t => ({ provider: t.provider, channel: t.channel, condition: t.condition, value: t.value, combine: t.combine })),
      actions:  r.actions.map(a  => ({ dest_channel: a.dest_channel, direction: a.direction, mode: a.mode, unit: a.unit, dest_value: a.dest_value, event_id: a.event_id, params: a.params, combine: a.combine, cascade: a.cascade })),
    }))
  );
  const isDirty = rulesSnapshot(rules) !== savedSnapshotRef.current;

  const resetState = () => {
    const blank = [makeRule(1)];
    setRules(blank);
    savedSnapshotRef.current = rulesSnapshot(blank);
    setExecutions([]);
    setShowExecDrawer(false);
    setShowConfirmClose(false);
    setEditingNameId(null);
    setConfirmAutoAcceptId(null);
  };

  const handleClose = () => {
    if (isDirty) { setShowConfirmClose(true); return; }
    resetState();
    onHide();
  };
  const handleForceClose = () => {
    resetState();
    onHide();
  };

  const _roles   = Array.isArray(userRole) ? userRole : (userRole ? [userRole] : []);
  const isViewer = _roles.length === 0 || !_roles.some(r => r === 'Manager' || r === 'Editor');

  // ── Channel / unit display labels (mirrors poster-worker-db.py) ──────────
  const CHANNEL_LABELS: Record<string, string> = {
    // Petrix telemetry channels — canonical names from config/channels.ts
    ...PETRIX_CHANNEL_LABELS,
    eff: 'Efficiency', pipe_frict: 'Pipe Friction',
    perf_frict: 'Perf Friction', ehd: 'EHD',
    step_up: 'Step Up',
    proppant_pct: 'Change Proppant Stage %',
    // Param-driven action channels — auto-populated from PETRIX_ACTIONS registry
    ...Object.fromEntries(PETRIX_ACTIONS.map(e => [e.channel, e.label])),
    fdi_magnitude:                   'FDI Magnitude',
    max_fdi_slope:                   'Max FDI Slope',
    vfr:                             'VFR',
    estimated_parent_child_distance: 'Est. Parent-Child Dist.',
    estimated_fdi_velocity:          'Est. FDI Velocity',
  };
  const UNIT_LABELS: Record<string, string> = {
    pct: '%', ppa: 'ppa', gpt: 'gpt', gpm: 'gpm', bpm: 'bpm',
  };

  // ── Theme tokens ──────────────────────────────────────────────────────────
  const BG_PAGE  = isDark ? '#1A1D1F' : '#f8f9fa';
  const BG_CARD  = isDark ? '#303335' : '#ffffff';
  const BG_RULE  = isDark ? '#3d4247' : '#f0f2f4';
  const BORDER   = isDark ? '#5c646b' : '#e6e6e6';
  const TEXT     = isDark ? '#e5e5e5' : '#303335';
  const MUTED    = isDark ? '#8b9197' : '#6c757d';
  const INPUT_BG = isDark ? '#303335' : '#ffffff';

  // ── Channel option lists ──────────────────────────────────────────────────
  const ruleChemChannels = chemChannels.filter(
    ch => !ch.value.endsWith('_rate') && !ch.value.endsWith('_total')
  );

  const triggerChannelOptions = [
    {
      label: 'Petrix',
      options: [
        ...PETRIX_BASE_CHANNELS,
        ...ruleChemChannels.map(ch => ({ value: ch.value, label: ch.label })),
      ].map(ch => ({ value: `petrix:${ch.value}`, label: ch.label })),
    },
    {
      label: 'Seismos',
      options: SEISMOS_CHANNELS.map(ch => ({ value: `seismos:${ch.value}`, label: ch.label })),
    },
    {
      label: 'Momentum',
      options: MOMENTUM_FDI_CHANNELS.map(ch => ({ value: `momentum:${ch.value}`, label: ch.label })),
    },
    {
      // value is already "scoreboard:<friendlyName>", the provider:channel format
      label: 'Scoreboard',
      options: scoreboardChannels.map(ch => ({ value: ch.value, label: ch.label })),
    },
  ];
  const flatTriggerOptions = triggerChannelOptions.flatMap(g => g.options);

  // Action dest options — Actions group driven by PETRIX_ACTIONS registry
  const destActionOptions = [
    {
      label: 'Channels',
      options: [
        { value: 'slurry_rate', label: 'Slurry Rate' },
        { value: 'stage_denso', label: 'Proppant Concentration' },
        ...ruleChemChannels.map(ch => ({ value: ch.value, label: ch.label })),
      ],
    },
    {
      label: 'Actions',
      options: [
        { value: 'step_up', label: 'Step Up' },
        { value: 'proppant_pct', label: 'Change Proppant Stage %' },
        ...PETRIX_ACTIONS.map(e => ({ value: e.channel, label: e.label })),
      ],
    },
  ];
  const flatDestOptions = destActionOptions.flatMap(g => g.options);

  // ── Scope option lists — from TreatmentDataGraph props ────────────────────
  const wellNameById = Object.fromEntries(wells.map(w => [w.id, w.well_name]));
  const scopeWellOptions = wells.map(w => ({ value: w.id, label: w.well_name }));
  const scopeStageGroups = wells.map(w => ({
    label: w.well_name,
    options: stages
      .filter(s => s.well_id === w.id)
      .sort((a, b) => a.stage_number - b.stage_number)
      .map(s => ({ value: s.id, label: `${w.well_name} · Stg ${s.stage_number}` })),
  })).filter(g => g.options.length > 0);
  const flatScopeStageOptions = scopeStageGroups.flatMap(g => g.options);

  // ── Load rules + executions on open; poll executions every 5 s ──────────
  useEffect(() => {
    if (!show || !padId) return;
    // Reset to blank state immediately so stale rules don't flash on reopen
    const blank = [makeRule(1)];
    setRules(blank);
    savedSnapshotRef.current = rulesSnapshot(blank);
    setExecutions([]);
    fetchRules();
    fetchExecutions();
    const interval = setInterval(fetchExecutions, 5_000);
    return () => clearInterval(interval);
  }, [show, padId]);

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res   = await axios.get(`${backendUrl.replace(/\/api$/, '')}/pads/${padId}/rules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.length) {
        // rule_order is presentation only — the worker ignores it. Sorted here
        // as well as server-side so a legacy row with a NULL order still lands
        // in a stable spot instead of wherever the query happened to put it.
        const ordered = [...res.data].sort(
          (a: any, b: any) => (a.rule_order ?? 0) - (b.rule_order ?? 0) || (a.id ?? 0) - (b.id ?? 0)
        );
        const mapped = ordered.map((r: any, i: number): Rule => ({
          id:                  uuidv4(),
          db_id:               r.id,
          name:                r.name ?? `Rule ${i + 1}`,
          is_active:           r.is_active ?? true,
          collapsed:           r.collapsed ?? false,
          fire_once_per_stage: r.fire_once_per_stage ?? false,
          rate_pressure_prediction_enabled: r.rate_pressure_prediction_enabled ?? false,
          // Mutually exclusive: auto-accept wins if a legacy row somehow has both,
          // otherwise both toggles would be hidden and the rule uneditable.
          fire_until_acknowledgement: (r.accept_on_expire_time ?? 0) > 0
            ? false
            : (r.fire_until_acknowledgement ?? false),
          accept_on_expire_time: (r.accept_on_expire_time ?? 0) > 0 ? r.accept_on_expire_time : null,
          cooldown_seconds:    r.cooldown_seconds ?? COOLDOWN_DEFAULT,
          created_by_user_id:  r.created_by_user_id ?? undefined,
          ...(() => {
            const scopes   = r.scopes ?? [];
            const wellIds  = scopes.filter((s: any) => s.well_id  != null).map((s: any) => s.well_id);
            const stageIds = scopes.filter((s: any) => s.stage_id != null).map((s: any) => s.stage_id);
            const scope_type: Rule['scope_type'] =
              scopes.some((s: any) => s.pad_id != null) || scopes.length === 0
                ? 'pad' : wellIds.length ? 'wells' : 'stages';
            return { scope_type, scope_well_ids: wellIds, scope_stage_ids: stageIds };
          })(),
          triggers: (r.triggers ?? []).map((t: any): TriggerRow => ({
            id: uuidv4(), provider: t.provider, channel: t.channel,
            condition: t.condition, value: String(t.condition_value ?? ''),
            combine: t.combine_with_next ?? 'none',
          })),
          actions: (r.actions ?? []).map((a: any): ActionRow => ({
            id:           uuidv4(),
            dest_channel: a.channel,
            direction:    a.direction   ?? '',
            mode:         a.mode        ?? '',
            unit:         a.unit        ?? '',
            dest_value:   String(a.dest_value ?? ''),
            event_id:     a.event_id    ?? null,
            params:       a.params
              ? (typeof a.params === 'string' ? JSON.parse(a.params) : a.params)
              : defaultParams(a.channel ?? ''),
            combine: (a.combine_with_next === 'and' ? 'and' : 'none') as 'none' | 'and',
            cascade: !!a.cascade_steps,
          })),
        }));
        setRules(mapped);
        savedSnapshotRef.current = rulesSnapshot(mapped);
      } else {
        const blank = [makeRule(1)];
        setRules(blank);
        savedSnapshotRef.current = rulesSnapshot(blank);
      }
    } catch (err) {
      console.error('Failed to load rules:', err);
    }
  };

  const fetchExecutions = async () => {
    try {
      const token = localStorage.getItem('token');
      const res   = await axios.get(`${backendUrl.replace(/\/api$/, '')}/pads/${padId}/rule-executions?limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExecutions(res.data?.items ?? []);
    } catch (err) {
      console.error('Failed to load rule executions:', err);
    }
  };

  // ── Trigger mutations ─────────────────────────────────────────────────────
  const updateTriggerField = useCallback((ruleId: string, triggerId: string, field: keyof TriggerRow, val: any) => {
    setRules(prev => prev.map(rule => {
      if (rule.id !== ruleId) return rule;
      const idx     = rule.triggers.findIndex(t => t.id === triggerId);
      const updated = rule.triggers.map(t => t.id === triggerId ? { ...t, [field]: val } : t);
      if (field === 'combine') {
        const sliced = updated.slice(0, idx + 1);
        return { ...rule, triggers: (val === 'and' || val === 'or') ? [...sliced, makeTrigger()] : sliced };
      }
      return { ...rule, triggers: updated };
    }));
  }, []);

  const handleTriggerChannel = useCallback((ruleId: string, triggerId: string, optValue: string) => {
    const colonIdx = optValue.indexOf(':');
    const provider = optValue.slice(0, colonIdx) as 'petrix' | 'seismos' | 'momentum' | 'scoreboard';
    const channel  = optValue.slice(colonIdx + 1);
    setRules(prev => prev.map(rule =>
      rule.id !== ruleId ? rule : {
        ...rule,
        triggers: rule.triggers.map(t => t.id !== triggerId ? t : {
          ...t,
          provider,
          channel,
          ...(channel === 'fdi_detected' ? { condition: '==', value: '0' } : {}),
          ...(channel === 'step_name' ? { condition: '==', value: '' } : {}),
        }),
      }
    ));
  }, []);

  // ── Action mutations ──────────────────────────────────────────────────────
  const updateActionField = useCallback((ruleId: string, actionId: string, field: keyof ActionRow, val: any) => {
    setRules(prev => prev.map(rule => {
      if (rule.id !== ruleId) return rule;
      const idx     = rule.actions.findIndex(a => a.id === actionId);
      const updated = rule.actions.map(a => {
        if (a.id !== actionId) return a;
        const next = { ...a, [field]: val };

        // When dest_channel changes, wire event_id + default params if it's a
        // registered param-driven action; clear them if it's a channel action.
        if (field === 'dest_channel') {
          const def = actionDef(val);
          if (def) {
            next.event_id  = def.event_id;   // null for schedule-building actions
            next.params    = defaultParams(val);
            next.direction = '';
            next.mode      = '';
            next.unit      = '';
            next.dest_value = '';
          } 
          else if (val === 'proppant_pct') {       
            next.event_id = null;                      
            next.params   = {};                       
            next.mode     = 'by';                      
            next.unit     = 'pct';                     
          } else {                                     
            next.event_id = null;
            next.params   = {};
          }
          if (!isCascadable(val)) next.cascade = false;
        }
        return next;
      });

      if (field === 'combine') {
        const sliced = updated.slice(0, idx + 1);
        return { ...rule, actions: val === 'and' ? [...sliced, makeAction()] : sliced };
      }
      return { ...rule, actions: updated };
    }));
  }, []);

  // ── Rule mutations ────────────────────────────────────────────────────────
  const addRule    = () => setRules(prev => [...prev, makeRule(prev.length + 1)]);
  const removeRule = (id: string) => setRules(prev => {
    const next = prev.filter(r => r.id !== id);
    return next.length > 0 ? next : [makeRule(1)];
  });
  const toggleActive = (id: string, v: boolean) =>
    setRules(prev => prev.map(r => r.id === id ? { ...r, is_active: v } : r));
  const toggleCollapsed = (id: string) =>
    setRules(prev => prev.map(r => r.id === id ? { ...r, collapsed: !r.collapsed } : r));

  // ── Drag reorder ──────────────────────────────────────────────────────────
  // Native HTML5 DnD — no new dependency. `draggable` is armed only while the
  // grip is held, otherwise the browser hijacks text selection inside the
  // rule's inputs. Rows reorder live on dragenter, so the list under the
  // cursor is already the result; drop just disarms.
  const [armedId, setArmedId] = useState<string | null>(null);
  const [dragId,  setDragId]  = useState<string | null>(null);

  const moveRule = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    setRules(prev => {
      const from = prev.findIndex(r => r.id === fromId);
      const to   = prev.findIndex(r => r.id === toId);
      if (from < 0 || to < 0) return prev;
      const next    = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  // ── Collapsed-header summary ──────────────────────────────────────────────
  // Both derived from state already in the component — no new endpoint.
  const scopeSummary = (r: Rule) =>
    r.scope_type === 'pad'   ? 'Entire pad'
  : r.scope_type === 'wells' ? `${r.scope_well_ids.length} well${r.scope_well_ids.length === 1 ? '' : 's'}`
  :                            `${r.scope_stage_ids.length} stage${r.scope_stage_ids.length === 1 ? '' : 's'}`;

  const lastExecution = (r: Rule): RuleExecution | null =>
    !r.db_id ? null : executions.reduce<RuleExecution | null>((best, e) =>
      Number(e.rule_id) !== Number(r.db_id) ? best
      : (!best || e.fired_at_ms > best.fired_at_ms) ? e : best, null);

  // Fixed mm/dd HH:MM rather than toLocale* — the chip sits in a narrow header
  // and locale formats vary in width enough to wrap it.
  const fmtFired = (ms: number) => {
    const d = new Date(ms);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const isBlankRule  = (rule: Rule) => !rule.db_id && rule.triggers.every(t => !t.channel);
  const buildPayload = () => rules.filter(r => !isBlankRule(r)).map((rule, i) => ({
    name:                rule.name,
    pad_id:              padId,
    is_active:           rule.is_active,
    rule_order:          i,
    collapsed:           rule.collapsed,
    fire_once_per_stage: rule.fire_once_per_stage,
    rate_pressure_prediction_enabled: rule.rate_pressure_prediction_enabled,
    fire_until_acknowledgement: rule.fire_until_acknowledgement,
    accept_on_expire_time: rule.accept_on_expire_time ?? -1,
    cooldown_seconds:    rule.cooldown_seconds,
    scopes: rule.scope_type === 'pad'
      ? [{ pad_id: padId }]
      : rule.scope_type === 'wells'
        ? rule.scope_well_ids.map(id => ({ well_id: id }))
        : rule.scope_stage_ids.map(id => ({ stage_id: id })),
    created_by_user_id:  rule.created_by_user_id ?? undefined,
    triggers: rule.triggers.map((t, i) => ({
      provider: t.provider || 'petrix', channel: t.channel,
      condition: t.condition, condition_value: parseFloat(t.value) || 0,
      combine_with_next: i < rule.triggers.length - 1 ? t.combine : null,
      order_index: i,
    })),
    actions: rule.actions.map((a, i) => ({
      channel:           a.dest_channel,
      // Every action fills either the scalar columns or params, never both.
      // Send whatever the row holds and let the backend fall empties to NULL —
      // no per-channel list to keep in sync here.
      direction:         a.direction,
      mode:              a.mode,
      unit:              a.unit,
      dest_value:        a.dest_value === '' ? null : (parseFloat(a.dest_value) || 0),
      event_id:          a.event_id ?? null,
      params:            a.params,
      combine_with_next: i < rule.actions.length - 1 ? a.combine : null,
      cascade_steps:     isCascadable(a.dest_channel) && a.cascade,
      order_index:       i,
    })),
  }));

  const handleSave = async () => {
    for (const rule of rules.filter(r => !isBlankRule(r))) {
      if (rule.scope_type === 'wells' && rule.scope_well_ids.length === 0)
        return void toast.warning(`Rule "${rule.name}" is scoped to Wells but no wells are selected`);
      if (rule.scope_type === 'stages' && rule.scope_stage_ids.length === 0)
        return void toast.warning(`Rule "${rule.name}" is scoped to Stages but no stages are selected`);
      for (const t of rule.triggers)
        if (!t.channel || !t.condition || !t.value)
          return void toast.warning(`Rule "${rule.name}" has an incomplete condition`);
      for (const a of rule.actions)
        if (!a.dest_channel)
          return void toast.warning(`Rule "${rule.name}" has an action with no channel or event selected`);
      // Only validate direction/mode/unit/value for plain channel actions.
      // Keyed off the registry rather than event_id: the drop actions have a
      // null event_id but carry no scalars, so an event_id test would wrongly
      // demand direction/mode/unit from them.
      for (const a of rule.actions)
        if (!actionDef(a.dest_channel) && a.dest_channel !== 'step_up'
            && (!a.direction || !a.mode || !a.unit || !a.dest_value))
          return void toast.warning(`Rule "${rule.name}" has an incomplete action`);
      for (const a of rule.actions) {
        const max = destValueMax(a.dest_channel);
        const v   = parseFloat(a.dest_value);
        if (max != null && !(v >= 0 && v <= max))
          return void toast.warning(`Rule "${rule.name}": ${flatDestOptions.find(o => o.value === a.dest_channel)?.label ?? a.dest_channel} must be greater than 0 and at most ${max}`);
      }
      // Drop actions: clean is required, and a sand slug needs a concentration
      // on at least one end or Petrix gets a 0 ppa proppant step. Concentration
      // is checked first — a zero conc is what leaves clean blank when the
      // operator entered lbs, so flagging clean would point at the wrong field.
      for (const a of rule.actions) {
        if (!DROP_CHANNELS.includes(a.dest_channel)) continue;
        if (a.dest_channel === 'sand_slug_drop'
            && !(parseFloat(a.params.start_conc) > 0 || parseFloat(a.params.end_conc) > 0))
          return void toast.warning(`Rule "${rule.name}": sand slug needs a start or end concentration`);
        if (!(parseFloat(a.params.clean) > 0))
          return void toast.warning(`Rule "${rule.name}": step clean must be greater than 0`);
      }
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${backendUrl.replace(/\/api$/, '')}/pads/${padId}/rules`,
        { rules: buildPayload() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rules saved successfully');
      await fetchRules();
      await fetchExecutions();
      setShowConfirmClose(false);
    } catch { toast.error('Failed to save rules'); }
    finally { setSaving(false); }
  };

  // ── Export / Import ───────────────────────────────────────────────────────
  const handleExport = () => {
    const blob = new Blob([JSON.stringify(buildPayload(), null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `rules_pad_${padId}_${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        const arr    = Array.isArray(parsed) ? parsed : [parsed];
        setRules(arr.map((r: any, i: number): Rule => ({
          id:                  uuidv4(),
          name:                r.name ?? `Rule ${i + 1}`,
          is_active:           r.is_active ?? true,
          collapsed:           r.collapsed ?? false,
          fire_once_per_stage: r.fire_once_per_stage ?? false,
          rate_pressure_prediction_enabled: r.rate_pressure_prediction_enabled ?? false,
          // Mutually exclusive: auto-accept wins if a legacy row somehow has both,
          // otherwise both toggles would be hidden and the rule uneditable.
          fire_until_acknowledgement: (r.accept_on_expire_time ?? 0) > 0
            ? false
            : (r.fire_until_acknowledgement ?? false),
          accept_on_expire_time: (r.accept_on_expire_time ?? 0) > 0 ? r.accept_on_expire_time : null,
          cooldown_seconds:    r.cooldown_seconds ?? COOLDOWN_DEFAULT,
          scope_type: 'pad' as const, scope_well_ids: [], scope_stage_ids: [],
          triggers: (r.triggers ?? []).map((t: any): TriggerRow => ({
            id: uuidv4(), provider: t.provider ?? 'petrix', channel: t.channel ?? '',
            condition: t.condition ?? '', value: String(t.condition_value ?? ''),
            combine: t.combine_with_next ?? 'none',
          })),
          actions: (r.actions ?? []).map((a: any): ActionRow => ({
            id:           uuidv4(),
            dest_channel: a.channel ?? '',
            direction:    a.direction  ?? '',
            mode:         a.mode       ?? '',
            unit:         a.unit       ?? '',
            dest_value:   String(a.dest_value ?? ''),
            event_id:     a.event_id   ?? null,
            params:       a.params
              ? (typeof a.params === 'string' ? JSON.parse(a.params) : a.params)
              : defaultParams(a.channel ?? ''),
            combine: a.combine_with_next === 'and' ? 'and' : 'none',
            cascade: !!a.cascade_steps,
          })),
        })));
        toast.success(`Imported ${arr.length} rule(s)`);
      } catch { toast.error('Invalid JSON file'); }
      e.target.value = '';
    };
    reader.readAsText(file);
  };

  // ── Execution log helpers ─────────────────────────────────────────────────
  const ruleNameById = (ruleId: number) =>
    rules.find(r => Number(r.db_id) === Number(ruleId))?.name ?? `Rule #${ruleId}`;

  const filteredExecs = executions
    .filter(e => execFilterRule   === 'all' || Number(e.rule_id) === Number(execFilterRule))
    .filter(e => execFilterResult === 'all' || (execFilterResult === 'accepted' ? e.mqtt_success === 1 : execFilterResult === 'timeout' ? e.mqtt_success === 2 : execFilterResult === 'rejected' ? e.mqtt_success === 0 : e.mqtt_success !== 1))
    .sort((a, b) => execSortDesc ? b.fired_at_ms - a.fired_at_ms : a.fired_at_ms - b.fired_at_ms);

  // Build a human-readable change string for any execution row.
  // event_id is stored in from_value; first param value in to_value.
  const execChangeStr = (ex: RuleExecution): string => {
    // mqtt_success=3 means the change never happened — Machina could not build
    // it, or Petrix refused it outright. The reason is the useful content on
    // every channel, so this is checked once here rather than per branch.
    if (ex.mqtt_success === 3) return ex.mqtt_error ?? 'Machina failed to send to ProPilot';
    if (ex.channel === 'step_up') return `Step Up → ${ex.to_value} bbl clean`;
    if (ex.channel === 'proppant_pct') {
      const dir = ex.direction === 'increase' ? 'Increased' : 'Decreased';
      const lbs = Math.round(Math.abs((ex.to_value ?? 0) - (ex.from_value ?? 0))).toLocaleString();
      return `${dir} by ${ex.dest_value}% (${lbs} lbs)`;
    }
    // Drop actions must be handled above the generic registry branch below —
    // they are in PETRIX_ACTIONS too, and params[0] would render nonsense.
    if (DROP_CHANNELS.includes(ex.channel)) {
      if (ex.mqtt_success === 3) return ex.mqtt_error ?? 'Machina failed to send to ProPilot';
      const resume = ex.to_value ? `, ${ex.to_value} bbl resumed after` : '';
      return `${CHANNEL_LABELS[ex.channel] ?? ex.channel} - ${ex.dest_value} bbl `
           + `inserted at ${ex.from_value} bbl clean${resume}`;
    }
    const evDef = PETRIX_ACTIONS.find(e => e.channel === ex.channel);
    if (evDef) {
      const slurryContext = ex.from_value != null
        ? ` (${ex.from_value.toFixed(1)} → ${ex.to_value.toFixed(1)} bpm)`
        : '';
      return `${evDef.label} — ${evDef.params[0].label}: ${ex.dest_value} ${evDef.params[0].unit}${slurryContext}`;
    }
    const displayVal = ex.mode === 'by' ? ex.dest_value : ex.to_value;
    return `${ex.direction} ${ex.mode} ${displayVal} ${UNIT_LABELS[ex.unit] ?? ex.unit}`;
  };

  const exportExecCsv = () => {
    const header = 'Time,Rule,Channel,Change,Result,Error';
    const rows = filteredExecs.map(ex => {
      const ts  = new Date(ex.fired_at_ms).toLocaleString();
      const lbl = CHANNEL_LABELS[ex.channel] ?? ex.channel;
      const chg = execChangeStr(ex);
      const res = ex.mqtt_success === 1 ? 'Accepted' : ex.mqtt_success === 2 ? 'Timed Out' : ex.mqtt_success === 3 ? 'Failed' : 'Rejected';
      const err = ex.mqtt_error ?? '';
      return [ts, ruleNameById(ex.rule_id), lbl, chg, res, err]
        .map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });
    const csv  = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `executions_pad_${padId}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Shared react-select styles ────────────────────────────────────────────
  const sel = {
    control: (b: any) => ({
      ...b,
      backgroundColor: INPUT_BG, borderColor: BORDER,
      minHeight: '32px', height: '32px', fontSize: '13px', boxShadow: 'none',
      '&:hover': { borderColor: isDark ? '#8b9197' : '#adb5bd' },
    }),
    valueContainer:      (b: any) => ({ ...b, padding: '0 6px 0 4px', flexWrap: 'nowrap' as const }),
    singleValue:         (b: any) => ({ ...b, color: TEXT, transform: 'translateX(6px)' }),
    placeholder:         (b: any) => ({ ...b, color: MUTED, fontSize: '12px', transform: 'translateX(6px)' }),
    input:               (b: any) => ({ ...b, color: TEXT, marginLeft: '6px' }),
    menu:                (b: any) => ({ ...b, backgroundColor: BG_CARD, border: `1px solid ${BORDER}`, zIndex: 9999, fontSize: '13px' }),
    menuPortal:          (b: any) => ({ ...b, zIndex: 9999 }),
    option:              (b: any, s: any) => ({
      ...b,
      backgroundColor: s.isSelected ? '#3E76FE' : s.isFocused ? (isDark ? '#3d4247' : '#f0f4ff') : BG_CARD,
      color: TEXT, cursor: 'pointer', fontSize: '13px',
    }),
    groupHeading:        (b: any) => ({
      ...b, color: isDark ? '#F9C844' : '#3E76FE', fontWeight: 700,
      fontSize: '10px', textTransform: 'uppercase' as const,
      letterSpacing: '0.08em', paddingLeft: '10px',
    }),
    indicatorsContainer: (b: any) => ({ ...b, height: '32px' }),
    indicatorSeparator:  () => ({ display: 'none' }),
    dropdownIndicator:   (b: any) => ({ ...b, padding: '0 6px', color: MUTED }),
  };

  const selMulti = {
    ...sel,
    control: (b: any) => ({ ...sel.control(b), height: 'auto', minHeight: '32px' }),
  };

  // ── Shared table / input styles ───────────────────────────────────────────
  const TH: React.CSSProperties = {
    backgroundColor: isDark ? '#2a2d2f' : '#f0f2f4',
    color: MUTED, fontSize: '11px', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    padding: '8px 8px', borderBottom: `1px solid ${BORDER}`,
    whiteSpace: 'nowrap',
  };
  const TD: React.CSSProperties = {
    padding: '4px 4px', verticalAlign: 'middle',
    borderBottom: `1px solid ${isDark ? '#3d4247' : '#f0f0f0'}`,
  };
  const INP: React.CSSProperties = {
    width: '100%', height: '32px', boxSizing: 'border-box',
    backgroundColor: INPUT_BG, border: `1px solid ${BORDER}`,
    borderRadius: '4px', color: TEXT, fontSize: '13px',
    padding: '0 10px', outline: 'none',
  };

  const mpProps = { menuPortalTarget: document.body, menuPosition: 'fixed' as const };

  const CombineBadge = ({ value }: { value: string }) => (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '2px 0' }}>
      <span style={{
        fontSize: '10px', fontWeight: 700, padding: '1px 12px', borderRadius: '10px',
        backgroundColor: value === 'and' ? 'rgba(62,118,254,0.15)' : 'rgba(249,200,68,0.15)',
        color:           value === 'and' ? '#3E76FE' : '#F9C844',
        border: `1px solid ${value === 'and' ? 'rgba(62,118,254,0.4)' : 'rgba(249,200,68,0.4)'}`,
        textTransform: 'uppercase' as const, letterSpacing: '0.08em',
      }}>{value}</span>
    </div>
  );

  const SectionLabel = ({ color, children }: { color: string; children: React.ReactNode }) => (
    <div style={{
      fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' as const,
      letterSpacing: '0.08em', color, marginBottom: '8px',
      display: 'flex', alignItems: 'center', gap: '8px',
    }}>
      <span style={{ width: '3px', height: '14px', borderRadius: '2px', backgroundColor: color, display: 'inline-block' }} />
      {children}
    </div>
  );

  const XBtn = ({ onClick }: { onClick: () => void }) => (
    <button onClick={onClick} style={{
      background: 'none', border: 'none', color: MUTED,
      cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px',
      display: 'flex', alignItems: 'center',
    }}>×</button>
  );

  const btnBase: React.CSSProperties = {
    background: 'none', border: `1px solid ${BORDER}`, borderRadius: '4px',
    color: MUTED, cursor: 'pointer', fontSize: '12px', padding: '5px 12px',
  };

  // ─────────────────────────────────────────────────────────────────────────
  // NOTE: Trigger rows and Action rows are rendered INLINE via .map() here
  // rather than as inner components. This prevents React from unmounting
  // input elements on every keystroke (which caused the cursor-loss bug).
  // ─────────────────────────────────────────────────────────────────────────

  const hasParamErrors = Object.keys(paramErrors).length > 0;

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
      <input ref={importRef} type="file" accept=".json"
        style={{ display: 'none' }} onChange={handleImport} />

      {/* Auto-accept confirmation — same full-screen overlay as the treatment schedule save */}
      {confirmAutoAcceptId && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          zIndex: 9999, backdropFilter: 'blur(4px)',
        }}>
          <h4 style={{ color: '#F9C430', fontWeight: 600, textAlign: 'center', margin: 0 }}>
            WARNING
          </h4>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center', margin: '0.5rem 0 1.5rem 0', fontSize: '1rem' }}>
            Intervention will fire without operator input. Are you sure you want to proceed?
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setConfirmAutoAcceptId(null)}
              style={{
                background: '#6c757d', border: 'none', borderRadius: '6px', color: '#fff',
                cursor: 'pointer', fontSize: '14px', fontWeight: 600, padding: '8px 22px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >Cancel</button>
            <button
              onClick={() => {
                setRules(prev => prev.map(r =>
                  r.id === confirmAutoAcceptId
                    ? { ...r, accept_on_expire_time: AUTO_ACCEPT_DEFAULT_S * 1000, fire_until_acknowledgement: false }
                    : r
                ));
                setConfirmAutoAcceptId(null);
              }}
              style={{
                background: '#8CC540', border: 'none', borderRadius: '6px', color: '#fff',
                cursor: 'pointer', fontSize: '14px', fontWeight: 600, padding: '8px 22px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >Proceed</button>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <Modal.Header closeButton style={{ backgroundColor: BG_CARD, borderBottom: `1px solid ${BORDER}` }}>
        <Modal.Title style={{ color: TEXT, fontSize: '15px', fontWeight: 600 }}>
          Rules Management
          {isDirty && (
            <span style={{
              marginLeft: '10px', fontSize: '11px', fontWeight: 500,
              color: '#F9C430', verticalAlign: 'middle',
            }}>● Unsaved changes</span>
          )}
        </Modal.Title>
      </Modal.Header>

      {/* ── Unsaved changes confirm banner ── */}
      {showConfirmClose && (
        <div style={{
          backgroundColor: isDark ? '#3a2e00' : '#fffbeb',
          borderBottom: `1px solid ${isDark ? '#7a6200' : '#fcd34d'}`,
          padding: '10px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
        }}>
          <span style={{ fontSize: '13px', color: isDark ? '#fcd34d' : '#92400e', fontWeight: 500 }}>
            You have unsaved changes. Close anyway?
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowConfirmClose(false)}
              style={{
                background: 'none', border: `1px solid ${isDark ? '#7a6200' : '#fcd34d'}`,
                borderRadius: '4px', color: isDark ? '#fcd34d' : '#92400e',
                cursor: 'pointer', fontSize: '12px', padding: '3px 12px',
              }}
            >Keep editing</button>
            <button
              onClick={handleForceClose}
              style={{
                background: '#dc3545', border: 'none', borderRadius: '4px',
                color: '#fff', cursor: 'pointer', fontSize: '12px', padding: '3px 12px',
              }}
            >Discard & close</button>
          </div>
        </div>
      )}

      {/* ── Body ── */}
      <Modal.Body style={{ backgroundColor: BG_PAGE, padding: '16px', maxHeight: '70vh', overflowY: 'auto' }}>
        {rules.map((rule) => (
          <div
            key={rule.id}
            draggable={armedId === rule.id}
            onDragStart={e => { setDragId(rule.id); e.dataTransfer.effectAllowed = 'move'; }}
            onDragEnter={() => dragId && moveRule(dragId, rule.id)}
            onDragOver={e => { if (dragId) e.preventDefault(); }}
            onDragEnd={() => { setDragId(null); setArmedId(null); }}
            onDrop={e => { e.preventDefault(); setDragId(null); setArmedId(null); }}
            style={{
              marginBottom: '16px',
              border: `1px solid ${dragId === rule.id ? (isDark ? '#378ADD' : '#185FA5') : BORDER}`,
              borderRadius: '8px', overflow: 'hidden', backgroundColor: BG_CARD,
              opacity: dragId === rule.id ? 0.65 : 1,
            }}>

            {/* ── Rule header bar ── */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 14px', backgroundColor: BG_RULE,
              borderBottom: rule.collapsed ? 'none' : `1px solid ${BORDER}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>

                {/* Drag grip — arms the card's draggable while held */}
                {!isViewer && (
                  <span
                    title="Drag to reorder"
                    onMouseDown={() => setArmedId(rule.id)}
                    onMouseUp={() => setArmedId(null)}
                    style={{
                      cursor: 'grab', color: MUTED, fontSize: '15px', lineHeight: 1,
                      userSelect: 'none', marginRight: '-4px',
                    }}
                  >⠿</span>
                )}

                {/* Collapse toggle */}
                <span
                  role="button"
                  title={rule.collapsed ? 'Expand rule' : 'Collapse rule'}
                  onClick={() => toggleCollapsed(rule.id)}
                  style={{
                    cursor: 'pointer', color: MUTED, fontSize: '10px', lineHeight: 1,
                    userSelect: 'none', width: '9px', display: 'inline-block',
                    marginRight: '-4px',
                  }}
                >{rule.collapsed ? '▶' : '▼'}</span>

                {/* Editable rule name */}
                {editingNameId === rule.id ? (
                  <input
                    autoFocus
                    value={rule.name}
                    onChange={e => setRules(prev => prev.map(r =>
                      r.id === rule.id ? { ...r, name: e.target.value } : r
                    ))}
                    onBlur={() => setEditingNameId(null)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingNameId(null); }}
                    style={{
                      backgroundColor: INPUT_BG, border: `1px solid ${BORDER}`,
                      borderRadius: '4px', color: TEXT, fontWeight: 700, fontSize: '13px',
                      padding: '2px 8px', outline: 'none', width: '160px',
                    }}
                  />
                ) : (
                  <span
                    title={isViewer ? undefined : 'Click to rename'}
                    onClick={() => !isViewer && setEditingNameId(rule.id)}
                    style={{
                      color: TEXT, fontWeight: 700, fontSize: '13px',
                      cursor: isViewer ? 'default' : 'pointer',
                    }}
                  >{rule.name}</span>
                )}

                {/* Active toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isViewer ? 'default' : 'pointer', marginBottom: 0 }}>
                  <Form.Check
                    type="switch" checked={rule.is_active}
                    onChange={e => !isViewer && toggleActive(rule.id, e.target.checked)}
                    disabled={isViewer}
                    style={{ cursor: isViewer ? 'default' : 'pointer' }}
                  />
                  <span style={{ fontSize: '12px', color: rule.is_active ? (isDark ? '#8CC540' : '#16a34a') : MUTED }}>
                    {rule.is_active ? 'Active' : 'Inactive'}
                  </span>
                </label>

                {/* Fire once per stage */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isViewer ? 'default' : 'pointer', marginBottom: 0 }}>
                  <input
                    type="checkbox" checked={rule.fire_once_per_stage}
                    onChange={e => !isViewer && setRules(prev => prev.map(r =>
                      r.id === rule.id ? { ...r, fire_once_per_stage: e.target.checked } : r
                    ))}
                    disabled={isViewer}
                    style={{ cursor: isViewer ? 'default' : 'pointer' }}
                  />
                  <span style={{ fontSize: '12px', color: MUTED }}>Once per stage</span>
                </label>

                {/* Block the rule when predicted pressure would exceed the well's max */}
                <label
                  title="When on, this rule will not fire if the predicted p90 pressure after the rate change would exceed the well's max treating pressure. When off, the prediction is still logged."
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isViewer ? 'default' : 'pointer', marginBottom: 0 }}
                >
                  <input
                    type="checkbox" checked={rule.rate_pressure_prediction_enabled}
                    onChange={e => !isViewer && setRules(prev => prev.map(r =>
                      r.id === rule.id ? { ...r, rate_pressure_prediction_enabled: e.target.checked } : r
                    ))}
                    disabled={isViewer}
                    style={{ cursor: isViewer ? 'default' : 'pointer' }}
                  />
                  <span style={{ fontSize: '12px', color: MUTED }}>Block on predicted pressure</span>
                </label>

                {/* Fire until acknowledgement — hidden while auto-accept is on, since
                    an auto-accepted rec never times out and so never re-sends. */}
                {(rule.accept_on_expire_time ?? 0) <= 0 && (
                  <label
                    title="If Petrix times the rec out, re-send it until the operator accepts or rejects. Stops when the stage changes."
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isViewer ? 'default' : 'pointer', marginBottom: 0 }}
                  >
                    <input
                      type="checkbox" checked={rule.fire_until_acknowledgement}
                      onChange={e => !isViewer && setRules(prev => prev.map(r =>
                        r.id === rule.id
                          ? { ...r, fire_until_acknowledgement: e.target.checked, accept_on_expire_time: e.target.checked ? -1 : r.accept_on_expire_time }
                          : r
                      ))}
                      disabled={isViewer}
                      style={{ cursor: isViewer ? 'default' : 'pointer' }}
                    />
                    <span style={{ fontSize: '12px', color: MUTED }}>Until acknowledged</span>
                  </label>
                )}

                {/* Auto-accept countdown — hidden while "until acknowledged" is on */}
                {!rule.fire_until_acknowledgement && (
                  <label
                    title="Petrix shows the operator a countdown, then fires the intervention automatically with no accept or reject. Leave off for the normal manual accept/reject."
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isViewer ? 'default' : 'pointer', marginBottom: 0 }}
                  >
                    <input
                      type="checkbox"
                      checked={(rule.accept_on_expire_time ?? 0) > 0}
                      onChange={e => {
                        if (isViewer) return;
                        if (e.target.checked) { setConfirmAutoAcceptId(rule.id); return; }
                        setRules(prev => prev.map(r =>
                          r.id === rule.id ? { ...r, accept_on_expire_time: -1 } : r
                        ));
                      }}
                      disabled={isViewer}
                      style={{ cursor: isViewer ? 'default' : 'pointer' }}
                    />
                    <span style={{ fontSize: '12px', color: MUTED }}>Auto-accept</span>
                  </label>
                )}

                {/* Countdown length — only shown when auto-accept is on */}
                {(rule.accept_on_expire_time ?? 0) > 0 && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0 }}>
                    <span style={{ fontSize: '12px', color: MUTED }}>after</span>
                    <input
                      type="number" min={AUTO_ACCEPT_MIN_S} max={AUTO_ACCEPT_MAX_S} step={1}
                      value={aoetSecs(rule.accept_on_expire_time) ?? AUTO_ACCEPT_DEFAULT_S}
                      onChange={e => {
                        const s = parseInt(e.target.value, 10);
                        const clamped = Number.isNaN(s)
                          ? AUTO_ACCEPT_DEFAULT_S
                          : Math.min(AUTO_ACCEPT_MAX_S, Math.max(AUTO_ACCEPT_MIN_S, s));
                        !isViewer && setRules(prev => prev.map(r =>
                          r.id === rule.id ? { ...r, accept_on_expire_time: clamped * 1000 } : r
                        ));
                      }}
                      disabled={isViewer}
                      style={{ ...INP, width: '56px', textAlign: 'center' }}
                    />
                    <span style={{ fontSize: '12px', color: MUTED }}>s</span>
                  </label>
                )}

                {/* Cooldown */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0 }}>
                  <span style={{ fontSize: '12px', color: MUTED, whiteSpace: 'nowrap' }}>Cooldown</span>
                  <input
                    type="number" min={COOLDOWN_MIN} step={10}
                    value={rule.cooldown_seconds}
                    onChange={e => !isViewer && setRules(prev => prev.map(r =>
                      r.id === rule.id ? { ...r, cooldown_seconds: Math.max(COOLDOWN_MIN, parseInt(e.target.value) || COOLDOWN_DEFAULT) } : r
                    ))}
                    disabled={isViewer}
                    style={{ ...INP, width: '64px', textAlign: 'center' }}
                  />
                  <span style={{ fontSize: '12px', color: MUTED }}>s</span>
                </label>
              </div>

              {/* Collapsed summary chips + delete rule */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {rule.collapsed && (() => {
                  const ex = lastExecution(rule);
                  const hue = !ex ? MUTED
                    : ex.mqtt_success === 1 ? '#8CC540'
                    : ex.mqtt_success === 3 ? '#fd7e14'
                    : '#dc3545';
                  const chip: React.CSSProperties = {
                    fontSize: '11px', borderRadius: '4px', padding: '2px 8px',
                    whiteSpace: 'nowrap', border: `1px solid ${BORDER}`,
                    backgroundColor: BG_CARD, color: MUTED,
                  };
                  return (
                    <>
                      <span style={chip}>{scopeSummary(rule)}</span>
                      <span
                        title={ex ? `${new Date(ex.fired_at_ms).toLocaleDateString()} ${new Date(ex.fired_at_ms).toLocaleTimeString()}` : undefined}
                        style={ex
                          ? { ...chip, color: hue, borderColor: hue }
                          : { ...chip, fontStyle: 'italic' }}
                      >
                        {ex
                          ? `Fired ${fmtFired(ex.fired_at_ms)}`
                          : rule.db_id ? 'Never fired' : 'Unsaved'}
                      </span>
                    </>
                  );
                })()}
                {!isViewer && (
                  <XBtn onClick={() => removeRule(rule.id)} />
                )}
              </div>
            </div>

            {/* ── Rule body ── */}
            {!rule.collapsed && (
            <div style={{ padding: '14px' }}>

              {/* SCOPE */}
              <SectionLabel color={isDark ? '#c084fc' : '#9333ea'}>Scope</SectionLabel>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '14px' }}>
                <div style={{ width: '160px', flexShrink: 0 }}>
                  <Select
                    options={SCOPE_TYPE_OPTIONS}
                    value={SCOPE_TYPE_OPTIONS.find(o => o.value === rule.scope_type) ?? SCOPE_TYPE_OPTIONS[0]}
                    onChange={opt => opt && !isViewer && setRules(prev => prev.map(r =>
                      r.id === rule.id
                        ? { ...r, scope_type: opt.value as Rule['scope_type'], scope_well_ids: [], scope_stage_ids: [] }
                        : r
                    ))}
                    styles={sel} {...mpProps}
                    isDisabled={isViewer}
                  />
                </div>
                {rule.scope_type === 'wells' && (
                  <div style={{ flex: 1 }}>
                    <Select
                      isMulti closeMenuOnSelect={false}
                      options={scopeWellOptions}
                      value={rule.scope_well_ids.map(id => ({
                        value: id, label: wellNameById[id] ?? `Well #${id}`,
                      }))}
                      onChange={opts => !isViewer && setRules(prev => prev.map(r =>
                        r.id === rule.id ? { ...r, scope_well_ids: (opts ?? []).map((o: any) => o.value) } : r
                      ))}
                      placeholder="Select wells…"
                      styles={selMulti} {...mpProps}
                      isDisabled={isViewer}
                    />
                  </div>
                )}
                {rule.scope_type === 'stages' && (
                  <div style={{ flex: 1 }}>
                    <Select
                      isMulti closeMenuOnSelect={false}
                      options={scopeStageGroups}
                      value={rule.scope_stage_ids.map(id => (
                        flatScopeStageOptions.find(o => o.value === id)
                          ?? { value: id, label: `Stage #${id}` }
                      ))}
                      onChange={opts => !isViewer && setRules(prev => prev.map(r =>
                        r.id === rule.id ? { ...r, scope_stage_ids: (opts ?? []).map((o: any) => o.value) } : r
                      ))}
                      placeholder="Select stages…"
                      styles={selMulti} {...mpProps}
                      isDisabled={isViewer}
                    />
                  </div>
                )}
              </div>

              {/* IF / WHEN */}
              <SectionLabel color={isDark ? '#3E76FE' : '#2563eb'}>When</SectionLabel>
              <div>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '35%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '4%'  }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={TH}>Channel</th>
                      <th style={TH}>Condition</th>
                      <th style={TH}>Value</th>
                      <th style={TH}>Combine</th>
                      <th style={{ ...TH, color: 'transparent' }}>-</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rule.triggers.map((t, tIdx) => {
                      const isLast = tIdx === rule.triggers.length - 1;
                      const rm = () => setRules(prev => prev.map(r =>
                        r.id !== rule.id ? r : { ...r, triggers: r.triggers.filter(x => x.id !== t.id) }
                      ));
                      const selectedTrigOpt = flatTriggerOptions.find(
                        o => o.value === `${t.provider}:${t.channel}`
                      ) ?? null;
                      return (
                        <React.Fragment key={t.id}>
                          <tr>
                            <td style={TD}>
                              <Select
                                options={triggerChannelOptions}
                                value={selectedTrigOpt}
                                onChange={opt => opt && handleTriggerChannel(rule.id, t.id, opt.value)}
                                placeholder="Provider : Channel" styles={sel} {...mpProps}
                                isDisabled={isViewer}
                              />
                            </td>
                            <td style={TD}>
                              <Select
                                options={t.channel === 'step_name'
                                  ? CONDITIONS.filter(c => c.value === '==' || c.value === '!=')
                                  : CONDITIONS}
                                value={CONDITIONS.find(c => c.value === t.condition) ?? null}
                                onChange={opt => opt && updateTriggerField(rule.id, t.id, 'condition', opt.value)}
                                placeholder="—" styles={sel} {...mpProps}
                                isDisabled={isViewer || t.channel === 'fdi_detected'}
                              />
                            </td>
                            <td style={TD}>
                              {t.channel === 'fdi_detected' ? (
                                <Select
                                  options={[{ value: '1', label: 'True' }, { value: '0', label: 'False' }]}
                                  value={t.value === '1'
                                    ? { value: '1', label: 'True' }
                                    : { value: '0', label: 'False' }
                                  }
                                  onChange={opt => opt && updateTriggerField(rule.id, t.id, 'value', opt.value)}
                                  styles={sel} {...mpProps}
                                  isDisabled={isViewer}
                                />
                              ) : t.channel === 'step_name' ? (
                                <Select
                                  options={STEP_NAME_OPTIONS}
                                  value={STEP_NAME_OPTIONS.find(o => o.value === t.value) ?? null}
                                  onChange={opt => opt && updateTriggerField(rule.id, t.id, 'value', opt.value)}
                                  styles={sel} {...mpProps}
                                  isDisabled={isViewer}
                                />
                              ) : (
                                <input
                                  type="number" step="0.001" min="0"
                                  value={t.value}
                                  onChange={e => updateTriggerField(rule.id, t.id, 'value', e.target.value)}
                                  placeholder="0.000"
                                  style={INP}
                                  disabled={isViewer}
                                />
                              )}
                            </td>
                            <td style={TD}>
                              {isLast
                                ? <Select
                                    options={TRIGGER_COMBINE_OPTIONS}
                                    value={TRIGGER_COMBINE_OPTIONS.find(c => c.value === t.combine) ?? TRIGGER_COMBINE_OPTIONS[0]}
                                    onChange={opt => opt && updateTriggerField(rule.id, t.id, 'combine', opt.value)}
                                    styles={sel} {...mpProps}
                                    isDisabled={isViewer}
                                  />
                                : <div style={{ height: '32px' }} />
                              }
                            </td>
                            <td style={{ ...TD, textAlign: 'center' }}>
                              {tIdx > 0 && !isViewer && <XBtn onClick={rm} />}
                            </td>
                          </tr>
                          {!isLast && t.combine !== 'none' && (
                            <tr>
                              <td colSpan={5} style={{ padding: 0, borderBottom: 'none' }}>
                                <CombineBadge value={t.combine} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* THEN divider */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                margin: '12px 0', color: MUTED, fontSize: '11px',
              }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: BORDER }} />
                <span style={{ fontWeight: 600, letterSpacing: '0.06em' }}>THEN</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: BORDER }} />
              </div>

              {/* ACTIONS */}
              <div>
                <SectionLabel color={isDark ? '#8CC540' : '#16a34a'}>Actions</SectionLabel>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '8%'  }} />
                    <col style={{ width: '9%'  }} />
                    <col style={{ width: '12%' }} />
                    {ruleHasCascade(rule.actions) && <col style={{ width: '8%' }} />}
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '4%'  }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={TH}>Channel / Event</th>
                      <th style={TH}>Direction</th>
                      <th style={TH}>Mode</th>
                      <th style={TH}>Unit</th>
                      <th style={TH}>Value</th>
                      {ruleHasCascade(rule.actions) && <th style={{ ...TH, textAlign: 'center' }}>Cascade</th>}
                      <th style={TH}>Combine</th>
                      <th style={{ ...TH, color: 'transparent' }}>-</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rule.actions.map((a, aIdx) => {
                      const isLast    = aIdx === rule.actions.length - 1;
                      const isStepUp  = a.dest_channel === 'step_up';
                      const isProppantPct  = a.dest_channel === 'proppant_pct';
                      // Any registry action — Petrix event or schedule-building drop —
                      // renders param inputs instead of the scalar columns. Those
                      // three cells are omitted entirely rather than dimmed, so the
                      // params get their width and the row stays 7 columns wide.
                      const actDef    = actionDef(a.dest_channel);
                      const isDimmed  = isStepUp;   // dim channel-action cols
                      const dimStyle: React.CSSProperties = isDimmed
                        ? { opacity: 0.25, pointerEvents: 'none', transition: 'opacity 0.15s' }
                        : {};
                      const rm = () => setRules(prev => prev.map(r =>
                        r.id !== rule.id ? r : { ...r, actions: r.actions.filter(x => x.id !== a.id) }
                      ));
                      return (
                        <React.Fragment key={a.id}>
                          <tr>
                            {/* Channel / Event — always active */}
                            <td style={TD}>
                              <Select
                                options={destActionOptions}
                                value={flatDestOptions.find(o => o.value === a.dest_channel) ?? null}
                                onChange={opt => {
                                  if (!opt) return;
                                  setParamErrors(prev => {
                                    const { [`${a.id}_dest_value`]: _, ...rest } = prev;
                                    return rest;
                                  });
                                  updateActionField(rule.id, a.id, 'dest_channel', opt.value);
                                }}
                                placeholder="Channel / Event" styles={sel} {...mpProps}
                                isDisabled={isViewer}
                              />
                            </td>

                            {/* Direction / Mode / Unit — omitted for registry actions;
                                the param cell below spans these four columns instead. */}
                            {!actDef && (
                            <td style={{ ...TD, ...dimStyle }}>
                              <Select
                                options={DIRECTIONS}
                                value={DIRECTIONS.find(d => d.value === a.direction) ?? null}
                                onChange={opt => opt && updateActionField(rule.id, a.id, 'direction', opt.value)}
                                placeholder="—" styles={sel} {...mpProps}
                                isDisabled={isViewer || isDimmed}
                              />
                            </td>
                            )}

                            {!actDef && (
                            <td style={{ ...TD, ...(isDimmed || isProppantPct ? dimStyle : {}) }}>
                              <Select
                                options={MODES}
                                value={isProppantPct
                                  ? { value: 'by', label: 'by' }
                                  : MODES.find(m => m.value === a.mode) ?? null
                                }
                                onChange={opt => opt && updateActionField(rule.id, a.id, 'mode', opt.value)}
                                placeholder="—" styles={sel} {...mpProps}
                                isDisabled={isViewer || isDimmed || isProppantPct}
                              />
                            </td>
                            )}

                            {!actDef && (
                            <td style={{ ...TD, ...(isDimmed || isProppantPct ? dimStyle : {}) }}>
                              <Select
                                options={UNITS}
                                value={isProppantPct
                                  ? { value: 'pct', label: '%' }
                                  : UNITS.find(u => u.value === a.unit) ?? null
                                }
                                onChange={opt => opt && updateActionField(rule.id, a.id, 'unit', opt.value)}
                                placeholder="—" styles={sel} {...mpProps}
                                isDisabled={isViewer || isDimmed || isProppantPct}
                              />
                            </td>
                            )}

                            {/* Value col — standard input for channel actions;
                                for registry actions this cell spans Direction,
                                Mode, Unit and Value and holds the param inputs.
                                Dimmed/empty for step_up. */}
                            {actDef ? (
                              <td style={TD} colSpan={4}>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {actDef.params.map(p => (
                                    <div key={p.key} style={{
                                      display: 'flex', flexDirection: 'column', gap: '2px',
                                      // Proppant names run long ("CS - 30/50 TerraProp"),
                                      // so the picker gets double width.
                                      flex: p.type === 'select' ? 2 : 1,
                                      // Sand slug carries five fields; floors let the row
                                      // wrap instead of crushing the inputs.
                                      minWidth: p.type === 'select' ? '140px' : '68px',
                                    }}>
                                      <label style={{ fontSize: '10px', color: MUTED, lineHeight: 1, marginBottom: '2px' }}>
                                        {p.label}{p.unit ? ` (${p.unit})` : ''}
                                      </label>
                                      {p.type === 'select' ? (
                                        // Empty string is a real choice here: it tells the
                                        // worker to inherit the sand type from the current
                                        // (or nearest preceding) proppant step.
                                        <Select
                                          options={[
                                            { value: '', label: 'Inherit from schedule' },
                                            ...(p.options ?? []).map(o => ({ value: o, label: o })),
                                          ]}
                                          value={(() => {
                                            const v = a.params[p.key] ?? p.default;
                                            return v
                                              ? { value: v, label: v }
                                              : { value: '', label: 'Inherit from schedule' };
                                          })()}
                                          onChange={opt => updateActionField(
                                            rule.id, a.id, 'params',
                                            syncParams(a.dest_channel, a.params, p.key, opt?.value ?? '')
                                          )}
                                          styles={sel} {...mpProps}
                                          isDisabled={isViewer}
                                        />
                                      ) : p.type === 'text' ? (
                                        <input
                                          type="text"
                                          placeholder="inherit from schedule"
                                          value={a.params[p.key] ?? p.default}
                                          onChange={e => updateActionField(
                                            rule.id, a.id, 'params',
                                            syncParams(a.dest_channel, a.params, p.key, e.target.value)
                                          )}
                                          style={{ ...INP, borderColor: BORDER }}
                                          disabled={isViewer}
                                        />
                                      ) : p.key === 'direction' ? (
                                        <Select
                                          options={[{ value: 'increase', label: 'Increase' }, { value: 'decrease', label: 'Decrease' }]}
                                          value={{ value: a.params[p.key] ?? p.default, label: (a.params[p.key] ?? p.default) === 'increase' ? 'Increase' : 'Decrease' }}
                                          onChange={opt => opt && updateActionField(rule.id, a.id, 'params', syncParams(a.dest_channel, a.params, p.key, opt.value))}
                                          styles={sel} {...mpProps}
                                          isDisabled={isViewer}
                                        />
                                      ) : (
                                        <input
                                          type="number" step={p.step ?? 1} min={p.min ?? 0} max={p.max}
                                          value={paramDisplayValue(a.dest_channel, a.params, p)}
                                          onChange={e => {
                                            const raw = parseFloat(e.target.value);
                                            const errKey = `${a.id}_${p.key}`;
                                            let errMsg = '';
                                            if (isNaN(raw)) { errMsg = `Must be a number`; }
                                            else if (p.min !== undefined && raw < p.min) { errMsg = `Min ${p.min} ${p.unit}`; }
                                            else if (p.max !== undefined && raw > p.max) { errMsg = `Max ${p.max} ${p.unit}`; }
                                            setParamErrors(prev => {
                                              const next = { ...prev };
                                              if (errMsg) next[errKey] = errMsg; else delete next[errKey];
                                              return next;
                                            });
                                            updateActionField(rule.id, a.id, 'params', syncParams(a.dest_channel, a.params, p.key, e.target.value));
                                          }}
                                          style={{ ...INP, borderColor: paramErrors[`${a.id}_${p.key}`] ? '#dc3545' : BORDER }}
                                          disabled={isViewer}
                                        />
                                      )}
                                      {paramErrors[`${a.id}_${p.key}`] && (
                                        <span style={{ fontSize: '10px', color: '#dc3545', lineHeight: 1.2, marginTop: '2px' }}>
                                          {paramErrors[`${a.id}_${p.key}`]}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            ) : (
                              <td style={{ ...TD, ...(isStepUp ? dimStyle : {}) }}>
                                <input
                                  type="number"
                                  step={isProppantPct ? 1 : 0.001}
                                  min={isProppantPct ? 1 : 0}
                                  max={isProppantPct ? 99 : (destValueMax(a.dest_channel) ?? undefined)}
                                  value={a.dest_value}
                                  onChange={e => {
                                    const rangeMax = destValueMax(a.dest_channel);
                                    if (isProppantPct || rangeMax != null) {
                                      const raw = parseFloat(e.target.value);
                                      const errKey = `${a.id}_dest_value`;
                                      let errMsg = '';
                                      if (isNaN(raw))  errMsg = 'Must be a number';
                                      else if (isProppantPct && raw < 1)  errMsg = 'Min 1%';
                                      else if (isProppantPct && raw > 99) errMsg = 'Max 99%';
                                      else if (rangeMax != null && raw < 0)        errMsg = 'Must be ≥ 0';
                                      else if (rangeMax != null && raw > rangeMax) errMsg = `Max ${rangeMax}`;
                                      setParamErrors(prev => {
                                        const next = { ...prev };
                                        if (errMsg) next[errKey] = errMsg; else delete next[errKey];
                                        return next;
                                      });
                                    }
                                    updateActionField(rule.id, a.id, 'dest_value', e.target.value);
                                  }}
                                  placeholder={isProppantPct ? '1–99' : '0.000'}
                                  style={{ ...INP, borderColor: paramErrors[`${a.id}_dest_value`] ? '#dc3545' : BORDER }}
                                  disabled={isViewer || isStepUp}
                                />
                                {paramErrors[`${a.id}_dest_value`] && (
                                  <span style={{ fontSize: '10px', color: '#dc3545', lineHeight: 1.2, marginTop: '2px', display: 'block' }}>
                                    {paramErrors[`${a.id}_dest_value`]}
                                  </span>
                                )}
                              </td>
                            )}

                            {/* Cascade — column shows when any action in the rule is
                                cascadable; the checkbox only on rows that are. */}
                            {ruleHasCascade(rule.actions) && (
                              <td style={{ ...TD, textAlign: 'center' }}>
                                {isCascadable(a.dest_channel) && (
                                  <input
                                    type="checkbox"
                                    checked={a.cascade}
                                    title="Apply to this step and every later step, so the change carries forward instead of reverting when the step ends"
                                    onChange={e => updateActionField(rule.id, a.id, 'cascade', e.target.checked)}
                                    disabled={isViewer}
                                    style={{ cursor: isViewer ? 'default' : 'pointer' }}
                                  />
                                )}
                              </td>
                            )}

                            {/* Combine — always its own cell. The param cell spans the
                                Direction/Mode/Unit/Value columns, never this one. */}
                            <td style={{ ...TD, ...dimStyle }}>
                              {isLast
                                ? <Select
                                    options={ACTION_COMBINE_OPTIONS}
                                    value={ACTION_COMBINE_OPTIONS.find(c => c.value === a.combine) ?? ACTION_COMBINE_OPTIONS[0]}
                                    onChange={opt => opt && updateActionField(rule.id, a.id, 'combine', opt.value)}
                                    styles={sel} {...mpProps}
                                    isDisabled={isViewer || isDimmed}
                                  />
                                : <div style={{ height: '32px' }} />
                              }
                            </td>

                            <td style={{ ...TD, textAlign: 'center' }}>
                              {aIdx > 0 && !isViewer && <XBtn onClick={rm} />}
                            </td>
                          </tr>
                          {!isLast && a.combine === 'and' && (
                            <tr>
                              <td colSpan={ruleHasCascade(rule.actions) ? 8 : 7} style={{ padding: 0, borderBottom: 'none' }}>
                                <CombineBadge value="and" />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>

                {/* Add action button */}
                {!isViewer && (
                  <button
                    onClick={() => setRules(prev => prev.map(r =>
                      r.id !== rule.id ? r : { ...r, actions: [...r.actions, makeAction()] }
                    ))}
                    style={{ ...btnBase, marginTop: '8px', fontSize: '11px', padding: '3px 10px' }}
                  >+ Add Action</button>
                )}
              </div>

              {/* EXECUTION LOG — visible once rule is saved (db_id exists) */}
              {rule.db_id && (
                <div style={{ marginTop: '14px' }}>
                  <SectionLabel color={isDark ? '#F9C844' : '#b45309'}>Execution Log</SectionLabel>
                  <div style={{
                    border: `1px solid ${BORDER}`, borderRadius: '6px', overflow: 'hidden',
                    maxHeight: '180px', overflowY: 'auto',
                  }}>
                    {executions.filter(e => Number(e.rule_id) === Number(rule.db_id)).length === 0 ? (
                      <div style={{
                        padding: '16px', textAlign: 'center',
                        fontSize: '12px', color: MUTED, fontStyle: 'italic',
                      }}>
                        No executions yet — log will populate once this rule fires
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ ...TH, width: '34%' }}>Time</th>
                            <th style={{ ...TH, width: '26%' }}>Channel</th>
                            <th style={{ ...TH, width: '20%' }}>Change</th>
                            <th style={{ ...TH, width: '12%', textAlign: 'center' }}>Result</th>
                          </tr>
                        </thead>
                        <tbody>
                          {executions
                            .filter(e => Number(e.rule_id) === Number(rule.db_id))
                            .map(ex => {
                              const ts  = new Date(ex.fired_at_ms);
                              const lbl = CHANNEL_LABELS[ex.channel] ?? ex.channel;
                              const chg = execChangeStr(ex);
                              return (
                                <tr key={ex.id}>
                                  <td style={{ ...TD, fontSize: '11px', color: MUTED }}>
                                    {ts.toLocaleDateString()} {ts.toLocaleTimeString()}
                                  </td>
                                  <td style={{ ...TD, fontSize: '12px', color: TEXT }}>{lbl}</td>
                                  <td style={{ ...TD, fontSize: '12px', color: TEXT }}>{chg}</td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    <span style={{
                                      fontSize: '10px', fontWeight: 700, padding: '1px 8px',
                                      borderRadius: '8px',
                                      backgroundColor: ex.mqtt_success === 1 ? 'rgba(140,197,64,0.15)' : ex.mqtt_success === 2 ? 'rgba(220,53,69,0.15)' : ex.mqtt_success === 3 ? 'rgba(253,126,20,0.15)' : 'rgba(220,53,69,0.15)',
                                      color:           ex.mqtt_success === 1 ? '#8CC540' : ex.mqtt_success === 2 ? '#dc3545' : ex.mqtt_success === 3 ? '#fd7e14' : '#dc3545',
                                      border: `1px solid ${ex.mqtt_success === 1 ? 'rgba(140,197,64,0.4)' : ex.mqtt_success === 2 ? 'rgba(220,53,69,0.4)' : ex.mqtt_success === 3 ? 'rgba(253,126,20,0.4)' : 'rgba(220,53,69,0.4)'}`,
                                    }}>
                                      {ex.mqtt_success === 1 ? 'Accepted' : ex.mqtt_success === 2 ? 'Timed Out' : ex.mqtt_success === 3 ? 'Failed' : 'Rejected'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

            </div>
            )}
          </div>
        ))}
      </Modal.Body>

      {/* ── Execution Log Drawer ── */}
      {showExecDrawer && (
        <div style={{
          backgroundColor: BG_CARD,
          borderTop: `2px solid ${isDark ? '#F9C844' : '#b45309'}`,
          display: 'flex', flexDirection: 'column',
          maxHeight: '340px',
        }}>
          {/* Drawer toolbar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 16px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: isDark ? '#F9C844' : '#b45309', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                All Executions
              </span>
              <select
                value={execFilterRule}
                onChange={e => setExecFilterRule(e.target.value)}
                style={{ ...INP, width: 'auto', fontSize: '12px', padding: '0 6px' }}
              >
                <option value="all">All rules</option>
                {rules.filter(r => r.db_id).map(r => (
                  <option key={r.db_id} value={String(r.db_id)}>{r.name}</option>
                ))}
              </select>
              <select
                value={execFilterResult}
                onChange={e => setExecFilterResult(e.target.value)}
                style={{ ...INP, width: 'auto', fontSize: '12px', padding: '0 6px' }}
              >
                <option value="all">All results</option>
                <option value="accepted">Accepted</option>
                <option value="timeout">Timed Out</option>
                <option value="rejected">Rejected</option>
              </select>
              <button
                onClick={() => setExecSortDesc(p => !p)}
                style={{ ...btnBase, fontSize: '11px', padding: '3px 10px', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {execSortDesc ? '↓ Newest first' : '↑ Oldest first'}
              </button>
              <span style={{ fontSize: '11px', color: MUTED }}>
                {filteredExecs.length} row{filteredExecs.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={exportExecCsv} style={{ ...btnBase, fontSize: '11px', padding: '3px 10px' }}>
                Export CSV
              </button>
              <button onClick={() => setShowExecDrawer(false)} style={{
                background: 'none', border: 'none', color: MUTED,
                cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 2px',
              }}>×</button>
            </div>
          </div>

          {/* Drawer table */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filteredExecs.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', fontSize: '12px', color: MUTED, fontStyle: 'italic' }}>
                No executions match the current filters
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ ...TH, width: '22%' }}>Time</th>
                    <th style={{ ...TH, width: '18%' }}>Rule</th>
                    <th style={{ ...TH, width: '18%' }}>Channel</th>
                    <th style={{ ...TH, width: '22%' }}>Change</th>
                    <th style={{ ...TH, width: '10%', textAlign: 'center' }}>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExecs.map(ex => {
                    const ts  = new Date(ex.fired_at_ms);
                    const lbl = CHANNEL_LABELS[ex.channel] ?? ex.channel;
                    const chg = execChangeStr(ex);
                    return (
                      <tr key={ex.id}>
                        <td style={{ ...TD, fontSize: '11px', color: MUTED }}>
                          {ts.toLocaleDateString()} {ts.toLocaleTimeString()}
                        </td>
                        <td style={{ ...TD, fontSize: '12px', color: TEXT }}>{ruleNameById(ex.rule_id)}</td>
                        <td style={{ ...TD, fontSize: '12px', color: TEXT }}>{lbl}</td>
                        <td style={{ ...TD, fontSize: '12px', color: TEXT }}>{chg}</td>
                        <td style={{ ...TD, textAlign: 'center' }}>
                          <span style={{
                            fontSize: '10px', fontWeight: 700, padding: '1px 8px', borderRadius: '8px',
                            backgroundColor: ex.mqtt_success === 1 ? 'rgba(140,197,64,0.15)' : ex.mqtt_success === 3 ? 'rgba(253,126,20,0.15)' : 'rgba(220,53,69,0.15)',
                            color:           ex.mqtt_success === 1 ? '#8CC540' : ex.mqtt_success === 3 ? '#fd7e14' : '#dc3545',
                            border: `1px solid ${ex.mqtt_success === 1 ? 'rgba(140,197,64,0.4)' : ex.mqtt_success === 3 ? 'rgba(253,126,20,0.4)' : 'rgba(220,53,69,0.4)'}`,
                          }}>
                            {ex.mqtt_success === 1 ? 'Accepted' : ex.mqtt_success === 3 ? 'Failed' : 'Rejected'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <Modal.Footer style={{
        backgroundColor: BG_CARD, borderTop: `1px solid ${BORDER}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 16px',
      }}>
        {!isViewer && (
          <button onClick={addRule} style={{ ...btnBase, color: TEXT }}>
            + Add Rule
          </button>
        )}
        {isViewer && <div />}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={() => setShowExecDrawer(p => !p)}
            style={{
              ...btnBase,
              color: showExecDrawer ? (isDark ? '#F9C844' : '#b45309') : MUTED,
              borderColor: showExecDrawer ? (isDark ? '#F9C844' : '#b45309') : BORDER,
            }}
          >
            {showExecDrawer ? '▲ Hide Executions' : '▼ View All Executions'}
          </button>
          {!isViewer && (
            <>
              <button onClick={handleExport} style={btnBase}>Export JSON</button>
              <button onClick={() => importRef.current?.click()} style={btnBase}>Import JSON</button>
              <button onClick={handleSave} disabled={saving || hasParamErrors} style={{
                backgroundColor: saving ? '#2d7a4a' : hasParamErrors ? '#5a5a5a' : '#8CC540',
                border: 'none', borderRadius: '4px', color: '#ffffff',
                cursor: (saving || hasParamErrors) ? 'not-allowed' : 'pointer',
                fontSize: '13px', fontWeight: 600, padding: '6px 18px',
                opacity: hasParamErrors ? 0.6 : 1,
              }}>
                {saving ? 'Saving…' : 'Save to Petrix'}
              </button>
            </>
          )}
          {isViewer && (
            <span style={{ fontSize: '12px', color: MUTED, fontStyle: 'italic' }}>
              View only — contact a Manager or Editor to make changes
            </span>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default RulesManagementModal;